/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { Download, Upload, CircleAlert } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { useGetBackup, useRestoreBackup } from "@/services/backupService";



const initialState = {
    backup_params: {
        include_db: "true",
        include_files: "true",
    },
    restore_params: {
        include_db: "true",
        include_files: "true",
        keep_passwords: "false",
    },
    on_download: false,
    on_upload: false,
};



const ACTIONS = {
    SET_BACKUP_INCLUDE_DB: "setBackupIncludeDB",
    SET_BACKUP_INCLUDE_FILES: "setBackupIncludeFiles",
    SET_RESTORE_INCLUDE_DB: "setRestoreIncludeDB",
    SET_RESTORE_INCLUDE_FILES: "setRestoreIncludeFiles",
    SET_RESTORE_KEEP_PASSWORDS: "setRestoreKeepPasswords",
    SET_ON_DOWNLOAD: "setOnDownload",
    SET_ON_UPLOAD: "setOnUpload",
    SET_BACKUP_ERROR: "setBackupError",
    SET_RESTORE_ERROR: "setRestoreError",
};



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_BACKUP_INCLUDE_DB:
            return { ...state, backup_params: { ...state.backup_params, include_db: action.payload } };
        case ACTIONS.SET_BACKUP_INCLUDE_FILES:
            return { ...state, backup_params: { ...state.backup_params, include_files: action.payload } };
        case ACTIONS.SET_RESTORE_INCLUDE_DB:
            return { ...state, restore_params: { ...state.restore_params, include_db: action.payload } };
        case ACTIONS.SET_RESTORE_INCLUDE_FILES:
            return { ...state, restore_params: { ...state.restore_params, include_files: action.payload } };
        case ACTIONS.SET_RESTORE_KEEP_PASSWORDS:
            return { ...state, restore_params: { ...state.restore_params, keep_passwords: action.payload } };
        case ACTIONS.SET_ON_DOWNLOAD:
            return { ...state, on_download: action.payload };
        case ACTIONS.SET_ON_UPLOAD:
            return { ...state, on_upload: action.payload };
        case ACTIONS.SET_BACKUP_ERROR:
            return { ...state, backup_error: action.payload };
        case ACTIONS.SET_RESTORE_ERROR:
            return { ...state, restore_error: action.payload };
        default:
            console.error("Unknown action.type " + action.type);
            throw new Error();
    }
};
    
export default function Index() {
    const [, setPageTitle] = useOutletContext();
    const [state, dispatch] = useReducer(reducer, initialState);

    const {getBackup, progress: downloadProgress} = useGetBackup()
    const {restoreBackup, progress: uploadProgress} = useRestoreBackup()

    const restoreFileInput = useRef(null);

    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Sauvegarde / Restauration");

        return () => {
            setPageTitle("Titre")
        }
    }, [setPageTitle]);

    const onGetBackup = async () => {
        dispatch({ type: ACTIONS.SET_ON_DOWNLOAD, payload: true });
        dispatch({ type: ACTIONS.SET_BACKUP_ERROR, payload: null });
        try {
            const backup = await getBackup(state.backup_params)

            // create blob from tar.gz and download it
            const url = URL.createObjectURL(backup);
            const link = document.createElement('a');
            link.href = url;
            link.download = "backup.tar.gz";
            document.body.appendChild(link);
            link.click();
            link.remove()
        } catch (e) {
            if (isAxiosError(e)) {
                dispatch({ type: ACTIONS.SET_BACKUP_ERROR, payload: axiosErrorToStringError(e) });    
            } else {
                console.log(e)
                dispatch({ type: ACTIONS.SET_BACKUP_ERROR, payload: e.message });
            }
        }
        dispatch({ type: ACTIONS.SET_ON_DOWNLOAD, payload: false });

    }

    const onRestoreBackup = async (file) => {
        dispatch({ type: ACTIONS.SET_ON_UPLOAD, payload: true });
        dispatch({ type: ACTIONS.SET_RESTORE_ERROR, payload: null });
        try {
            await restoreBackup(file, state.restore_params)
        } catch (e) {
            if (isAxiosError(e)) {
                dispatch({ type: ACTIONS.SET_RESTORE_ERROR, payload: axiosErrorToStringError(e) });    
            } else {
                console.log(e)
                dispatch({ type: ACTIONS.SET_RESTORE_ERROR, payload: e.message });
            }
        }
        dispatch({ type: ACTIONS.SET_ON_UPLOAD, payload: false });
    }


    return (
        <div className="flex flex-row justify-around">
            <div className="flex flex-col w-[40%] border border-secondary rounded-xl p-4 items-center gap-2">
                <h2 className="flex flex-row text-secondary flex-0 text-lg flex-none content-center w-full gap-2">
                    <Download />Sauvegarde
                </h2>
                <div className="flex flex-col flex-1 gap-2 w-full justify-center">
                    { state?.on_download && 
                        <div className="flex flex-col">
                            <div className="w-full h-6 border border-gray-300 rounded overflow-hidden">
                                <div
                                    className="h-full bg-blue-600 text-white text-sm text-center transition-all duration-200"
                                    style={{ width: `${downloadProgress}%` }}
                                >
                                    {downloadProgress} %
                                </div>
                            </div>
                        </div>
                    }
                    { !state?.on_download &&
                        <>
                            <div className="flex flex-row gap-2 justify-center items-center">
                                <label className="text-primary text-sm" htmlFor="includeDB">
                                    Inclure la base de données
                                </label>
                                <input
                                    type="checkbox"
                                    id="includeDB"
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    checked={state.backup_params.include_db === "true"}
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_BACKUP_INCLUDE_DB, payload: e.target.checked ? "true" : "false" })}
                                />
                            </div>
                            <div className="flex flex-row gap-2 justify-center items-center">
                                <label className="text-primary text-sm" htmlFor="includeFiles">
                                    Inclure les fichiers
                                </label>
                                <input
                                    type="checkbox"
                                    id="includeFiles"
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    checked={state.backup_params.include_files === "true"}
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_BACKUP_INCLUDE_FILES, payload: e.target.checked ? "true" : "false" })}
                                />
                            </div>
                            <div className="flex flex-row gap-2 justify-center mt-5">
                                <button className="flex flex-0 rounded-xl border border-primary hover:bg-blue-300 p-2 gap-2" onClick={onGetBackup}>
                                    Télécharger la sauvegarde
                                </button>
                            </div>
                        </>
                    }

                    { state?.backup_error && 
                        <div className="flex flex-row gap-2 justify-center items-center rounded-xl border border-red-500 text-red-500 p-2 mt-5">
                            <CircleAlert />
                            {state.backup_error}
                        </div>
                    }
                </div>
            </div>
            <div className="flex flex-col w-[40%] border border-secondary rounded-xl p-4 items-center">
                <h2 className="flex text-secondary text-lg flex-none content-center w-full gap-2">
                    <Upload />Restauration
                </h2>
                <div className="flex flex-col flex-1 gap-2 w-full justify-center">
                    { state?.on_upload && 
                        <div className="flex flex-col">
                            <div className="w-full h-6 border border-gray-300 rounded overflow-hidden">
                                <div
                                    className="h-full bg-blue-600 text-white text-sm text-center transition-all duration-200"
                                    style={{ width: `${uploadProgress}%` }}
                                >
                                    {uploadProgress} %
                                </div>
                            </div>
                        </div>
                    }
                    { !state?.on_upload &&
                        <>
                            <div className="flex flex-row gap-2 justify-center items-center mt-5">
                                <label className="text-primary text-sm" htmlFor="includeDB">
                                    Inclure la base de données
                                </label>
                                <input
                                    type="checkbox"
                                    id="includeDB"
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    checked={state.restore_params.include_db === "true"}
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_RESTORE_INCLUDE_DB, payload: e.target.checked ? "true" : "false" })}
                                />
                            </div>
                            <div className="flex flex-row gap-2 justify-center items-center">
                                <label className="text-primary text-sm" htmlFor="includeFiles">
                                    Inclure les fichiers
                                </label>
                                <input
                                    type="checkbox"
                                    id="includeFiles"
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    checked={state.restore_params.include_files === "true"}
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_RESTORE_INCLUDE_FILES, payload: e.target.checked ? "true" : "false" })}
                                />
                            </div>

                            <div className="flex flex-row gap-2 justify-center items-center">
                                <label className="text-primary text-sm" htmlFor="includeFiles">
                                    Garder les mots de passe
                                </label>
                                <input
                                    type="checkbox"
                                    id="includeFiles"
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    checked={state.restore_params.keep_passwords === "true"}
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_RESTORE_KEEP_PASSWORDS, payload: e.target.checked ? "true" : "false" })}
                                />
                            </div>

                            <div className="flex flex-col gap-2 justify-center items-center mt-5">
                                <label className="text-primary text-sm" htmlFor="includeFiles">
                                    Choisissez un fichier de sauvegarde
                                </label>
                                <input
                                    type="file"
                                    id="file"
                                    ref={restoreFileInput}
                                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                                    placeholder="Choisissez un fichier de sauvegarde"
                                    onChange={(e) => console.log(e.target.files)}
                                />
                            </div>

                            <div className="flex flex-row gap-2 justify-center items-center mt-5">
                                <button className="flex flex-0 rounded-xl border border-primary hover:bg-red-300 p-2 gap-2" onClick={() => onRestoreBackup(restoreFileInput.current.files[0])}>
                                    Restaurer la sauvegarde
                                </button>
                            </div>
                            
                        </>
                    }

                    { state?.restore_error && 
                        <div className="flex flex-row gap-2 justify-center items-center rounded-xl border border-red-500 text-red-500 p-2 mt-5">
                            <CircleAlert />
                            {state.restore_error}
                        </div>
                    }

                </div>
                
            </div>
        </div>
    )
}